import { ChevronRightIcon, FileTextIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

import { ChatAgentItemLayout } from './chat-agent-item-layout.atom'
import type { ChatContentBrief, ContentBriefClickHandler } from './chat-message.interface'

export interface ChatContentBriefItemProps {
  contentBrief: ChatContentBrief
  agentName?: string
  isPinned?: boolean
  isHighlighted?: boolean
  onPin?: (itemId: string) => void
  onClick?: ContentBriefClickHandler
  className?: string
}

const truncateExcerpt = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength).trim()}...`
}

const ChatContentBriefItemBase: React.FC<ChatContentBriefItemProps> = ({
  contentBrief,
  agentName,
  isPinned = false,
  isHighlighted = false,
  onPin,
  onClick,
  className,
}): ReactNode => {
  const { name, version, excerpt } = contentBrief

  const handleClick = onClick ? () => onClick(contentBrief) : undefined

  return (
    <ChatAgentItemLayout
      itemId={contentBrief.id}
      timestamp={contentBrief.timestamp}
      agentName={agentName}
      isPinned={isPinned}
      isHighlighted={isHighlighted}
      onPin={onPin}
      onClick={handleClick}
      wrapperClassName="chat-content-brief"
      cardClassName={cn('chat-content-brief-card flex-col gap-2')}
      className={className}
    >
      <div className="flex items-center gap-3">
        <div className="flex size-8 flex-shrink-0 items-center justify-center rounded-md bg-primary/10">
          <FileTextIcon className="size-4 text-primary" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
            <Badge variant="secondary" className="flex-shrink-0 text-xs">
              v{version}
            </Badge>
          </div>
        </div>

        {onClick && (
          <ChevronRightIcon className="size-4 flex-shrink-0 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground" />
        )}
      </div>

      {excerpt && (
        <p className="text-xs leading-relaxed text-muted-foreground">{truncateExcerpt(excerpt)}</p>
      )}
    </ChatAgentItemLayout>
  )
}

export const ChatContentBriefItem = memo(ChatContentBriefItemBase)

ChatContentBriefItem.displayName = 'ChatContentBriefItem'
